import DealFormFields from './DealFormFields';
import { PartyField, PartyFormSection } from '../../../../shared/components/PartyFormFields';
import EntityMultiSelect from '../../../../shared/components/EntityMultiSelect';
import { formatDateForInput } from '../utils/dealAdapters';
import EntityPartyLinks from '../../../../shared/components/EntityPartyLinks';
import DealCommentsSection from './sections/DealCommentsSection';
import React, { useState } from 'react';
import { DealAccountingContent } from './DealAccountingSection';
import useDealAccountingPayments from '../hooks/useDealAccountingPayments';
import '../../styles/deal-detail.css';
import { Handshake } from '../../../../shared/assets/AppIcons';
import {
  CreateFormDialog,
  CreateFormCancelButton,
  CreateFormSubmitButton,
} from '../../../../shared/components/CreateFormDialog';
import { DEAL_STATUS_OPTIONS } from '../utils/dealAdapters';
import { IconActionButton } from '@/shared/components/patterns';

import EntityDetailDialog from '@/shared/components/EntityDetailDialog';
import { useDealDetailState } from '@/modules/deals/shared/hooks/useDealDetailState';

import DealReadOnlyView from './sections/DealReadOnlyView';
import ClientCustomFieldsSection from '../../../clients/components/ClientCustomFieldsSection';
import {
  archiveDealsCustomField,
  createDealsCustomField,
  listDealsCustomFields,
} from '../../services/deals-custom-fields.service';

const DEAL_CUSTOM_FIELDS_API = {
  list: listDealsCustomFields,
  create: createDealsCustomField,
  archive: archiveDealsCustomField,
};

export default function BaseDealDetailDialog({
  deal,
  isOpen,
  onClose,
  focusComments = false,
  onSave,
  onMove,
  onOpenClient,
  onOpenStaff,
  dealType = 'inCorso',
  staff = [],
  clients = [],
  allowEditing = true,
  allowTypeChange = true,
  extraActions = null,
  primaryActions = null,
  ordinaryActions = null,
  dangerActions = null,
  customEditActions = null,
  extraContent = null,
  workflowContent = null,
}) {
  const {
    isEditing,
    isSaving,
    setIsEditing,
    editedDeal,
    commentsState,
    newDealType,
    setNewDealType,
    updateField,
    handleClientRelink,
    addScadenza,
    updateScadenza,
    installmentDrafts, beginInstallmentAmountEdit, updateInstallmentAmount, commitInstallmentAmount,
    removeScadenza,
    handleAddComment,
    handleAddReply,
    handleSave,
    handleCancel,
  } = useDealDetailState({
    deal,
    dealType,
    clients,
    onClose,
    onSave,
    onMove,
  });

  const [saveError, setSaveError] = useState('');
  const paymentState = useDealAccountingPayments(deal, dealType, isOpen && !isEditing);

  if (!deal || !editedDeal) return null;

  function navigateToClient() {
    onOpenClient?.(editedDeal.clientId, editedDeal.client);
  }

  function navigateToStaff(staffId, staffMember) {
    onOpenStaff?.(staffId, staffMember);
  }

  const editActions = allowEditing ? (
    <IconActionButton
      iconKey="actions.edit"
      label="Modifica affare"
      onClick={() => {
        setSaveError('');
        setIsEditing(true);
      }}
      disabled={isSaving}
    />
  ) : null;
  const resolvedPrimaryActions = primaryActions || extraActions;
  const resolvedEditActions = editActions || customEditActions;

  if (isEditing)
    return (
      <CreateFormDialog
        open={isOpen}
        className="deal-create-dialog"
        busy={isSaving}
        title="Modifica affare"
        icon={Handshake}
        onOpenChange={(next) => {
          if (!next) handleCancel();
        }}
        footer={
          <>
            <CreateFormCancelButton onClick={handleCancel} disabled={isSaving} />
            <CreateFormSubmitButton form="edit-deal-form" saving={isSaving}>
              Salva affare
            </CreateFormSubmitButton>
          </>
        }
      >
        {saveError ? (
          <p role="alert" className="custom-fields-section__error wt-inline-error">
            {saveError}
          </p>
        ) : null}
        <form
          id="edit-deal-form"
          className="deal-create-form"
          onSubmit={async (event) => {
            event.preventDefault();
            setSaveError('');
            try {
              const result = await handleSave();
              if (result?.ok === false) setSaveError(result.message || 'Salvataggio non riuscito.');
            } catch (error) {
              setSaveError(error.message || 'Salvataggio non riuscito.');
            }
          }}
        >
          <DealFormFields dealType={dealType} formState={editedDeal} staff={staff} clients={clients}
            isSubmitting={isSaving} updateField={updateField} handleClientSelect={handleClientRelink}
            {...{ addScadenza, updateScadenza, updateInstallmentAmount, beginInstallmentAmountEdit,
              commitInstallmentAmount, installmentDrafts, removeScadenza }}
            extraFields={<PartyFormSection title="Informazioni aggiuntive">
              <div className="party-form__row">
                {allowTypeChange && <label>Stato affare
                  <select value={newDealType} disabled={isSaving} onChange={event => setNewDealType(event.target.value)}>
                    {DEAL_STATUS_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>}
                <PartyField id="deal-start-date" type="date" label={dealType === 'proposta' ? 'Data proposta' : 'Data inizio'}
                  value={formatDateForInput(editedDeal[dealType === 'proposta' ? 'proposalDate' : 'dataInizio'])}
                  onChange={value => updateField(dealType === 'proposta' ? 'proposalDate' : 'dataInizio', value)} disabled={isSaving} />
              </div>
              <PartyField id="deal-contact" label="Contatto" value={editedDeal.clientContact}
                onChange={value => updateField('clientContact', value)} disabled={isSaving} />
              <div className="party-form__row">
                <PartyField id="deal-contact-email" type="email" label="Email contatto" value={editedDeal.clientEmail}
                  onChange={value => updateField('clientEmail', value)} disabled={isSaving} />
                <PartyField id="deal-contact-phone" type="tel" label="Telefono contatto" value={editedDeal.clientPhone}
                  onChange={value => updateField('clientPhone', value)} disabled={isSaving} />
              </div>
              <div className="party-form__field"><label htmlFor="deal-team">Personale coinvolto</label>
                <EntityMultiSelect id="deal-team" label="Personale coinvolto" options={staff.map(member => ({ ...member, id: member.name }))}
                  value={editedDeal.teamCoinvolto || []} onChange={value => updateField('teamCoinvolto', value)} disabled={isSaving} />
              </div>
            </PartyFormSection>} />

        </form>
        <DealCommentsSection key={editedDeal.id} comments={editedDeal.commenti || []} commentsState={commentsState}
          onAddComment={handleAddComment} onAddReply={handleAddReply} />
      </CreateFormDialog>
    );

  return (
    <EntityDetailDialog
      isOpen={isOpen}
      onClose={onClose}
      title={editedDeal.name || 'Dettaglio affare'}
      subtitle={DEAL_STATUS_OPTIONS.find((option) => option.value === dealType)?.label}
      avatarLabel={(editedDeal.name || 'A').charAt(0).toUpperCase()}
      badges={[editedDeal.tipoAffare, editedDeal.stage].filter(Boolean).map((label) => ({ label, tone: 'active' }))}
      primaryActions={resolvedPrimaryActions}
      actions={ordinaryActions}
      editActions={resolvedEditActions}
      dangerActions={dangerActions}
      className="deal-detail-dialog"
    >
      <div className="deal-sheet__layout">
        {workflowContent}
        <DealReadOnlyView
          deal={editedDeal}
          dealType={dealType}
          staff={staff}
          onNavigateClient={editedDeal.clientId && onOpenClient ? navigateToClient : undefined}
          onNavigateStaff={onOpenStaff ? navigateToStaff : undefined}
          payments={paymentState.payments}
        />
        <ClientCustomFieldsSection
          readOnly
          formKey={dealType === 'proposta' ? 'deals.proposal.create' : 'deals.in-progress.create'}
          values={editedDeal.customFields || {}}
          api={DEAL_CUSTOM_FIELDS_API}
        />
        <DealAccountingContent deal={deal} dealType={dealType} {...paymentState} />
        {extraContent ? <div className="deal-sheet__extra-content">{extraContent}</div> : null}
        <EntityPartyLinks entity={editedDeal} clients={clients} staff={staff} />
        <div className="deal-sheet__comments">
          <DealCommentsSection
            autoFocus={focusComments}
            key={editedDeal.id}
            comments={editedDeal.commenti || []}
            commentsState={commentsState}
            onAddComment={handleAddComment}
            onAddReply={handleAddReply}
          />
        </div>
      </div>
    </EntityDetailDialog>
  );
}
